import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPublishedArticles,
  fetchMyArticles,
  fetchEditorQueue,
  fetchArticleById,
  createArticle,
  updateArticle,
  submitArticle,
  cancelSubmission,
  deleteArticle,
  publishArticle,
  rejectArticle,
  uploadArticleImage,
} from "./articlesActions";

const initialState = {
  // Public: published articles
  published: [],
  publishedLoading: false,
  publishedError: null,

  // Podcaster: own articles list
  myArticles: [],
  myArticlesLoading: false,
  myArticlesError: null,

  // Editor: pending review queue
  queue: [],
  queueLoading: false,
  queueError: null,

  // Single article being viewed or edited
  current: null,
  currentLoading: false,
  currentError: null,

  // Create
  createLoading: false,
  createError: null,

  // Save/update
  saveLoading: false,
  saveError: null,

  // Status transitions (submit, cancel, publish, reject)
  actionLoading: false,
  actionError: null,

  // Image upload
  imageUploading: false,
  imageError: null,
};

// Helper: upsert an article into an array by id
const upsert = (arr, article) => {
  const idx = arr.findIndex((a) => a.id === article.id);
  if (idx !== -1) {
    arr[idx] = article;
  } else {
    arr.push(article);
  }
};

const articlesSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    clearCurrent(state) {
      state.current = null;
      state.currentError = null;
    },
    clearActionError(state) {
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    // fetchPublishedArticles
    builder
      .addCase(fetchPublishedArticles.pending, (state) => {
        state.publishedLoading = true;
        state.publishedError = null;
      })
      .addCase(fetchPublishedArticles.fulfilled, (state, action) => {
        state.publishedLoading = false;
        // Support both paginated { results: [] } and plain array responses
        state.published = Array.isArray(action.payload)
          ? action.payload
          : action.payload.results ?? [];
      })
      .addCase(fetchPublishedArticles.rejected, (state, action) => {
        state.publishedLoading = false;
        state.publishedError = action.payload;
      });

    // fetchMyArticles
    builder
      .addCase(fetchMyArticles.pending, (state) => {
        state.myArticlesLoading = true;
        state.myArticlesError = null;
      })
      .addCase(fetchMyArticles.fulfilled, (state, action) => {
        state.myArticlesLoading = false;
        state.myArticles = action.payload;
      })
      .addCase(fetchMyArticles.rejected, (state, action) => {
        state.myArticlesLoading = false;
        state.myArticlesError = action.payload;
      });

    // fetchEditorQueue
    builder
      .addCase(fetchEditorQueue.pending, (state) => {
        state.queueLoading = true;
        state.queueError = null;
      })
      .addCase(fetchEditorQueue.fulfilled, (state, action) => {
        state.queueLoading = false;
        state.queue = action.payload;
      })
      .addCase(fetchEditorQueue.rejected, (state, action) => {
        state.queueLoading = false;
        state.queueError = action.payload;
      });

    // fetchArticleById
    builder
      .addCase(fetchArticleById.pending, (state) => {
        state.currentLoading = true;
        state.currentError = null;
      })
      .addCase(fetchArticleById.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchArticleById.rejected, (state, action) => {
        state.currentLoading = false;
        state.currentError = action.payload;
      });

    // createArticle
    builder
      .addCase(createArticle.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createArticle.fulfilled, (state, action) => {
        state.createLoading = false;
        state.current = action.payload;
        upsert(state.myArticles, action.payload);
      })
      .addCase(createArticle.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      });

    // updateArticle (auto-save)
    builder
      .addCase(updateArticle.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.current = action.payload;
        upsert(state.myArticles, action.payload);
      })
      .addCase(updateArticle.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError = action.payload;
      });

    // submitArticle
    builder
      .addCase(submitArticle.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(submitArticle.fulfilled, (state, action) => {
        state.actionLoading = false;
        upsert(state.myArticles, action.payload);
        if (state.current?.id === action.payload.id) {
          state.current = action.payload;
        }
      })
      .addCase(submitArticle.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // cancelSubmission
    builder
      .addCase(cancelSubmission.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(cancelSubmission.fulfilled, (state, action) => {
        state.actionLoading = false;
        upsert(state.myArticles, action.payload);
        if (state.current?.id === action.payload.id) {
          state.current = action.payload;
        }
      })
      .addCase(cancelSubmission.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // deleteArticle
    builder
      .addCase(deleteArticle.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.myArticles = state.myArticles.filter(
          (a) => a.id !== action.payload.id
        );
        if (state.current?.id === action.payload.id) {
          state.current = null;
        }
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // publishArticle
    builder
      .addCase(publishArticle.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(publishArticle.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Update status in queue so Published tab shows it
        upsert(state.queue, action.payload);
        upsert(state.myArticles, action.payload);
      })
      .addCase(publishArticle.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // rejectArticle
    builder
      .addCase(rejectArticle.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(rejectArticle.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Remove from queue after rejection
        state.queue = state.queue.filter((a) => a.id !== action.payload.id);
      })
      .addCase(rejectArticle.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // uploadArticleImage
    builder
      .addCase(uploadArticleImage.pending, (state) => {
        state.imageUploading = true;
        state.imageError = null;
      })
      .addCase(uploadArticleImage.fulfilled, (state) => {
        state.imageUploading = false;
      })
      .addCase(uploadArticleImage.rejected, (state, action) => {
        state.imageUploading = false;
        state.imageError = action.payload;
      });
  },
});

export const { clearCurrent, clearActionError } = articlesSlice.actions;
export const selectArticles = (state) => state.articles;
export default articlesSlice.reducer;
